// BUILD-042 Owner-led onboarding — HR Admin "Form Library". Authority:
// docs/superpowers/decisions/2026-10-07-build-042-owner-led-onboarding-documents-and-test-roster.md
//   October 8 22:34 Phoenix addendum: "discoverable HR Admin -> Form Library... Upload a blank
//   PDF, give it a clear title, preview/download it. Maintain replacement versions without
//   losing earlier records. Choose an employee and assign a form."
//   October 10 12:24 Phoenix addendum: compact rows (title/description/current version plus
//   Preview, Download, Assign, Replace, and a less-prominent Retire); "Add a form" and
//   "Replace" each open a dialog rather than an always-visible inline form; Retire requires an
//   explicit second-click confirmation; Preview opens the file inline in a new tab, Download
//   forces a named save.
//   October 10 14:05/14:08 Phoenix addenda (this pass): the redundant inner "Library" heading
//   is replaced by compact search over title/description/filename; a small gray archive/Retire
//   icon sits beside Replace (accessible name + tooltip); a collapsed "See retired forms
//   (count)" lives below "How this works"; retired forms keep their Preview/Download/version
//   info; a confirmed, audited HR Reactivate reverses a retired template without auto-
//   assigning it or restoring any employee record. "Add a form" also accepts an explicit,
//   HR-set-only equipment classification (required by the "Equipment returned" history reason
//   — never guessed from the form's name).
//
// HR uploads a reusable blank document (Employee Handbook, Office Equipment Checkout, or any
// PDF Chris/Builder prepare), uploads a new version when the wording changes, retires one from
// future assignment — never deleting a prior version — and can preview/download any version
// directly from the Library itself (independent of any employee assignment). Assigning a form
// to a specific employee happens right here too, via the SAME existing assignHrDocument path
// AdminDocumentsSection already uses (reused, not duplicated) — the actual employee-side
// download/upload/review workflow still lives on that Employee's own "Documents & Signatures"
// section, which this Library links out to after a successful assignment.
//
// Reuse, not a new product: this is PDF-upload-plus-metadata on the EXISTING template/version/
// storage/permission/assignment path. No form builder, no OCR, no Word conversion, no digital
// signing, no asset inventory, no bulk-onboarding engine. Chris/Builder prepare and clean up the
// actual PDF files separately — this Library never seeds, invents, or auto-assigns any form.
import { useEffect, useId, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  listHrDocumentTemplates, createHrDocumentTemplate, createHrDocumentTemplateVersion, updateHrDocumentTemplate,
  retireHrDocumentTemplate, reactivateHrDocumentTemplate, downloadHrDocumentTemplateVersion, assignHrDocument, listHrLiveSigningConfigs,
  type HrDocumentTemplate, type HrLiveSigningConfigSummary,
} from '../../api/client';
import { SigningSetupPanel } from './SigningSetupPanel';
import { AssignValueInputs } from './AssignValueInputs';
import { missingAssignmentValues } from './formFieldsModel';
import { CardTitle } from './workspace/SectionHeading';
import { EmployeePicker } from './EmployeePicker';
import { checkDownloadUrl } from './workspace/documentFileCheck';
import { formatLocalDate } from './hrFormat';
import { runEditTemplateSave } from './editTemplateSave';
import { actionErrorMessage, classifyLoadError, needsPermissionMessage, PERMISSION_LABEL, useSurfaceFlag, type LoadView } from './workspace/loadState';
import { LoadViewNotice, PermissionHint } from './workspace/LoadNotices';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── "Add a form" dialog ─────────────────────────────────────────────────────────────────────
function AddFormModal({ onClose, onCreated }: { onClose: () => void; onCreated: (message: string) => void }) {
  const nameId = useId();
  const descriptionId = useId();
  const equipmentId = useId();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isEquipment, setIsEquipment] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (!name.trim() || !file || creating) return;
    setCreating(true);
    setError(null);
    try {
      const fileContentBase64 = await fileToBase64(file);
      await createHrDocumentTemplate({ name: name.trim(), description: description.trim() || undefined, fileName: file.name, fileContentBase64, contentType: file.type || 'application/pdf', recordKind: isEquipment ? 'equipment' : null });
      onCreated(`"${name.trim()}" added to the Form Library.`);
      onClose();
    } catch (e: any) {
      setError(actionErrorMessage(e, PERMISSION_LABEL.documents, 'add forms', 'Failed to add the form.'));
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="add-form-title" onClick={onClose}>
      <div className="bg-white rounded-lg border border-gray-200 shadow-lg w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 border-b border-gray-100">
          <h2 id="add-form-title" className="text-sm font-semibold text-gray-700">Add a form</h2>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label htmlFor={nameId} className="text-xs text-gray-500 block mb-1">Title</label>
            <input id={nameId} autoFocus className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full" placeholder="e.g. Employee Handbook" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label htmlFor={descriptionId} className="text-xs text-gray-500 block mb-1">Description (optional)</label>
            <input id={descriptionId} className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Blank form file (PDF, JPEG, PNG, or HEIC)</label>
            <input type="file" accept="application/pdf,image/jpeg,image/png,image/heic" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm w-full" />
            {file && <div className="text-xs text-gray-500 mt-1">Selected file: <span className="font-medium text-gray-700">{file.name}</span></div>}
          </div>
          <div className="flex items-start gap-2">
            <input id={equipmentId} type="checkbox" checked={isEquipment} onChange={(e) => setIsEquipment(e.target.checked)} className="mt-0.5" />
            <label htmlFor={equipmentId} className="text-xs text-gray-500">
              This form acknowledges company equipment (e.g. a checkout/issuance form). Needed only to later use the "Equipment returned" History reason — never guessed from the title.
            </label>
          </div>
          {error && <div className="text-xs text-red-700">{error}</div>}
        </div>
        <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm text-gray-600 px-4 py-1.5 rounded hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={handleSubmit} disabled={creating || !name.trim() || !file} className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed">
            {creating ? 'Adding…' : 'Add to Library'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── "Replace" dialog — upload a new version of an existing form ───────────────────────────
function ReplaceFormModal({ template, onClose, onReplaced }: { template: HrDocumentTemplate; onClose: () => void; onReplaced: (message: string) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latest = template.versions[0];

  async function handleConfirm() {
    if (!file || uploading) return;
    setUploading(true);
    setError(null);
    try {
      const fileContentBase64 = await fileToBase64(file);
      await createHrDocumentTemplateVersion(template.templateId, { fileName: file.name, fileContentBase64, contentType: file.type || 'application/pdf' });
      onReplaced('New version uploaded. The earlier version is retained, not replaced — both stay on file and remain individually previewable.');
      onClose();
    } catch (e: any) {
      setError(actionErrorMessage(e, PERMISSION_LABEL.documents, 'replace forms', 'Failed to upload the replacement version.'));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="replace-form-title" onClick={onClose}>
      <div className="bg-white rounded-lg border border-gray-200 shadow-lg w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 border-b border-gray-100">
          <h2 id="replace-form-title" className="text-sm font-semibold text-gray-700">Replace "{template.name}"</h2>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="text-xs text-gray-500">
            {template.description && <div className="mb-1">{template.description}</div>}
            Current version: v{latest?.versionNumber} ({latest?.fileName})
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">New file (PDF, JPEG, PNG, or HEIC)</label>
            <input type="file" accept="application/pdf,image/jpeg,image/png,image/heic" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm w-full" />
            {file && <div className="text-xs text-gray-500 mt-1">Selected file: <span className="font-medium text-gray-700">{file.name}</span></div>}
          </div>
          <p className="text-[11px] text-gray-400">
            This adds a new version — v{(latest?.versionNumber ?? 0) + 1}. The current version
            (v{latest?.versionNumber}) and every earlier version stay on file and remain
            individually previewable; nothing is overwritten or deleted. Any Employee already
            assigned this form keeps the exact version they were given.
          </p>
          {error && <div className="text-xs text-red-700">{error}</div>}
        </div>
        <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm text-gray-600 px-4 py-1.5 rounded hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={handleConfirm} disabled={uploading || !file} className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed">
            {uploading ? 'Uploading…' : 'Confirm replacement'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── "Edit form" dialog (approved) — prefilled title/description, optional replacement file ───
// Metadata-only edits (no file chosen) call updateHrDocumentTemplate and create NO version, so
// earlier versions and every employee record are untouched. If a replacement file IS chosen, the
// metadata is saved first and then a NEW version is uploaded via the SAME createHrDocument
// TemplateVersion path "Replace" uses — earlier versions and existing employee assignments are
// still preserved (an assignment keeps the exact version it was given).
function EditFormModal({ template, onClose, onSaved }: { template: HrDocumentTemplate; onClose: () => void; onSaved: (message: string) => void }) {
  const nameId = useId();
  const descriptionId = useId();
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Partial-save baseline. When the metadata POST succeeds but the file-replacement upload then
  // fails, the metadata is already persisted. We advance this baseline to the ACTUALLY-persisted
  // values returned by the API so (a) `metadataChanged` recomputes false and a retry sends ONLY
  // the file (never re-POSTing metadata that already saved), and (b) the dialog honestly reflects
  // what landed. `savedBaseline` starts as the template's original persisted values.
  const [savedBaseline, setSavedBaseline] = useState<{ name: string; description: string | null }>({
    name: template.name,
    description: template.description ?? null,
  });
  // Post-metadata-save file-upload state. Distinguishes two very different failure classes so the
  // dialog never makes a false claim and a retry never blindly duplicates a version:
  //   'none'      — no pending file problem.
  //   'retryable' — the upload DEFINITELY did not create a version (a 4xx rejection before any
  //                 write, or a pre-send client error). Safe to retry by re-uploading the file.
  //   'uncertain' — the upload outcome is UNKNOWN (network/timeout with no HTTP response, or a 5xx
  //                 after the request reached the server). A version MAY have been created. We must
  //                 NOT claim "nothing uploaded" and must NOT blindly create another version — the
  //                 user is directed to refresh and check version history before deciding.
  const [fileUploadState, setFileUploadState] = useState<'none' | 'retryable' | 'uncertain'>('none');
  const metadataSavedPendingFile = fileUploadState !== 'none';
  const latest = template.versions[0];

  const metadataChanged = name.trim() !== savedBaseline.name || (description.trim() || null) !== savedBaseline.description;

  async function handleSave() {
    if (!name.trim() || saving) return;
    if (!metadataChanged && !file) { onClose(); return; }
    setSaving(true);
    setError(null);
    // Delegate the metadata-then-file sequencing, partial-save baseline advance, and upload-failure
    // classification to the shared pure logic (editTemplateSave.runEditTemplateSave) — the exact
    // code the tests exercise. This component only maps the structured outcome to UI state/messages.
    const outcome = await runEditTemplateSave(
      {
        updateMetadata: (input) => updateHrDocumentTemplate(template.templateId, input),
        encodeFile: (f) => fileToBase64(f),
        createVersion: (input) => createHrDocumentTemplateVersion(template.templateId, input),
      },
      { name, description, file, baseline: savedBaseline }
    );
    setSaving(false);

    // Always reflect any advanced baseline so a retry never re-POSTs already-saved metadata.
    setSavedBaseline(outcome.kind === 'noop' || outcome.kind === 'metadata-error' ? savedBaseline : outcome.baseline);
    if (outcome.kind !== 'noop' && outcome.kind !== 'metadata-error') {
      setName(outcome.baseline.name);
      setDescription(outcome.baseline.description ?? '');
    }

    switch (outcome.kind) {
      case 'noop':
        onClose();
        return;
      case 'metadata-error':
        setError(outcome.message || 'Failed to save changes.');
        return;
      case 'success': {
        setFile(null);
        setFileUploadState('none');
        const msg = outcome.versionCreated
          ? (outcome.metadataSaved
              ? 'Changes saved and a new version uploaded. Earlier versions are retained, and any employee already assigned this form keeps the exact version they were given.'
              : 'New version uploaded. Earlier versions are retained, and any employee already assigned this form keeps the exact version they were given.')
          : 'Changes saved. No new version was created — the title/description changed, every file version stays exactly as it was.';
        onSaved(msg);
        onClose();
        return;
      }
      case 'file-retryable': {
        setFileUploadState('retryable');
        const savedPrefix = outcome.metadataSaved ? 'Your title and description were saved. ' : '';
        setError(
          savedPrefix +
          'The file replacement was rejected before any new version was created, so nothing was duplicated. ' +
          'Press "Retry file upload" to try the file again' + (outcome.metadataSaved ? ', or Cancel to keep the saved title/description without a new file version.' : '.')
        );
        return;
      }
      case 'file-uncertain': {
        setFileUploadState('uncertain');
        const savedPrefix = outcome.metadataSaved ? 'Your title and description were saved. ' : '';
        setError(
          savedPrefix +
          'The file upload did not complete and its result is uncertain — a new version MAY or MAY NOT have been created. ' +
          'To avoid creating a duplicate, this will not retry automatically. Press "Refresh & review versions" to reload the version history and check whether the new version is already present before trying again.' +
          (outcome.message ? ` (${outcome.message})` : '')
        );
        return;
      }
    }
  }

  // Dismissing the dialog. If metadata was already persisted during a partial save, tell the parent
  // (so the list refreshes to the saved title/description) rather than silently closing on a stale
  // list; otherwise close plainly. Note the message is state-accurate: for an uncertain upload we
  // do NOT assert the file was not uploaded.
  function handleClose() {
    if (fileUploadState === 'retryable') {
      onSaved('Title/description saved. The file replacement was not uploaded, so the current file version is unchanged.');
    } else if (fileUploadState === 'uncertain') {
      onSaved('Title/description saved. The file upload result was uncertain — please check the version history to confirm whether a new version was created.');
    }
    onClose();
  }

  // The primary button's job depends on the failure class. For an uncertain upload, the safe action
  // is NOT to re-create a version but to refresh and let the user verify — so it closes with a
  // parent refresh instead of calling handleSave again.
  function handlePrimary() {
    if (fileUploadState === 'uncertain') { handleClose(); return; }
    handleSave();
  }
  const primaryLabel = saving
    ? 'Saving…'
    : fileUploadState === 'uncertain'
      ? 'Refresh & review versions'
      : fileUploadState === 'retryable'
        ? 'Retry file upload'
        : 'Save changes';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="edit-form-title" onClick={handleClose}>
      <div className="bg-white rounded-lg border border-gray-200 shadow-lg w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 border-b border-gray-100">
          <h2 id="edit-form-title" className="text-sm font-semibold text-gray-700">Edit "{template.name}"</h2>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label htmlFor={nameId} className="text-xs text-gray-500 block mb-1">Title</label>
            <input id={nameId} autoFocus className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label htmlFor={descriptionId} className="text-xs text-gray-500 block mb-1">Description (optional)</label>
            <input id={descriptionId} className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Replace the file (optional — PDF, JPEG, PNG, or HEIC)</label>
            <input type="file" accept="application/pdf,image/jpeg,image/png,image/heic" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm w-full" />
            {file && <div className="text-xs text-gray-500 mt-1">New file: <span className="font-medium text-gray-700">{file.name}</span></div>}
          </div>
          <p className="text-[11px] text-gray-400">
            Changing only the title or description does <span className="font-medium">not</span> create a new version — current version v{latest?.versionNumber} stays in place.
            Choosing a replacement file adds v{(latest?.versionNumber ?? 0) + 1}; every earlier version stays on file, and any Employee already assigned this form keeps the exact version they were given.
          </p>
          {error && <div className={`text-xs ${metadataSavedPendingFile ? 'text-amber-700' : 'text-red-700'}`}>{error}</div>}
        </div>
        <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={handleClose} className="text-sm text-gray-600 px-4 py-1.5 rounded hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={handlePrimary} disabled={saving || (fileUploadState !== 'uncertain' && (!name.trim() || (!metadataChanged && !file)))} className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed">
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// Oct 17: small inline icons for the one-line form rows (decorative; every icon button has an aria-label and a tooltip).
const ICON_BTN = 'w-8 h-8 inline-flex items-center justify-center rounded text-gray-500 hover:text-[#176A9B] hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]';
const svgProps = { className: 'w-[18px] h-[18px]', fill: 'none', viewBox: '0 0 24 24', strokeWidth: 1.6, stroke: 'currentColor', 'aria-hidden': true } as const;
function EyeIcon() { return <svg {...svgProps}><path strokeLinecap="round" strokeLinejoin="round" d="M2.04 12.32a1.01 1.01 0 0 1 0-.64C3.42 7.51 7.36 4.5 12 4.5c4.64 0 8.57 3.01 9.96 7.18.07.21.07.43 0 .64C20.58 16.49 16.64 19.5 12 19.5c-4.64 0-8.57-3.01-9.96-7.18Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>; }
function DownloadIcon() { return <svg {...svgProps}><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>; }
function UserPlusIcon() { return <svg {...svgProps}><path strokeLinecap="round" strokeLinejoin="round" d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" /></svg>; }
function TrashIcon() { return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>; }
function ChevronIcon({ open }: { open: boolean }) { return <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="m9 5 7 7-7 7" /></svg>; }

export function DocumentTemplatesPanel() {
  const navigate = useNavigate();
  // Cycle 12: opened from an employee's guided setup (Manage forms) - a one-click way back to that employee's setup step (router state only; nothing is stored).
  const cameFrom = (useLocation().state as { returnTo?: string; employeeName?: string } | null) ?? null;
  // Cycle 12: which form's "Suggest for onboarding" save is in flight, and the last refusal per form (the checkbox always shows the SAVED value).
  const [suggestBusy, setSuggestBusy] = useState<string | null>(null);
  const [suggestError, setSuggestError] = useState<Record<string, string>>({});
  const [templates, setTemplates] = useState<HrDocumentTemplate[] | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [downloadNotice, setDownloadNotice] = useState<Record<string, string>>({});
  const [historyOpenId, setHistoryOpenId] = useState<string | null>(null);
  const [assignOpenId, setAssignOpenId] = useState<string | null>(null);
  // Oct 17: which form rows have their drop-down open (all collapsed by default = one clean line per form).
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) => setExpandedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [assigning, setAssigning] = useState(false);
  // Expanded fields (Oct 16): values HR types for fields filled in at assignment time (frozen with the assignment).
  const [assignValues, setAssignValues] = useState<Record<string, string>>({});
  const [retiredOpen, setRetiredOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [replaceModalTemplateId, setReplaceModalTemplateId] = useState<string | null>(null);
  const [editModalTemplateId, setEditModalTemplateId] = useState<string | null>(null);
  // October 10 12:24 addendum: Retire now requires an explicit second click — the row no
  // longer disappears on the first click. This holds which template's inline confirmation
  // (if any) is currently showing.
  const [retireConfirmId, setRetireConfirmId] = useState<string | null>(null);
  // October 10 14:08 addendum: compact search over title/description/filename, replacing the
  // redundant inner "Library" heading.
  const [search, setSearch] = useState('');
  const [reactivating, setReactivating] = useState<string | null>(null);
  // BUILD-042 (Oct 15 22:12): which document VERSIONS currently have a live e-signature setup, and the open "Set up signing" screen.
  const [liveSigning, setLiveSigning] = useState<Record<string, HrLiveSigningConfigSummary>>({});
  const [signingSetupTarget, setSigningSetupTarget] = useState<{ templateId: string; versionId: string } | null>(null);

  const refreshSigning = () => listHrLiveSigningConfigs()
    .then((d) => setLiveSigning(Object.fromEntries(d.configs.map((c) => [c.templateVersionId, c]))))
    .catch(() => setLiveSigning({}));
  // Oct 24: loading / ready (possibly empty) / denied / unavailable are distinct. A refused or failed FIRST read is never drawn as "No forms yet"; a failed RE-read
  // keeps what is already on screen. Controls that change the library are disabled, with the reason, when the server reported the caller lacks the permission.
  const [view, setView] = useState<LoadView>({ kind: 'loading' });
  const cannotManage = useSurfaceFlag('mayManageDocuments') === false;
  const permissionMessage = needsPermissionMessage(PERMISSION_LABEL.documents, 'change the Form Library');
  const refresh = () => {
    void refreshSigning();
    return listHrDocumentTemplates()
      .then((d) => { setTemplates(d.templates); setView({ kind: 'ready' }); })
      .catch((e) => setView((v) => (v.kind === 'ready' ? v : classifyLoadError(e, { gatedList: true }))));
  };
  useEffect(() => { refresh(); }, []);

  async function handleConfirmRetire(templateId: string) {
    try {
      await retireHrDocumentTemplate(templateId);
      setStatus('Retired — no longer assignable going forward. Existing assignments and all history are unaffected; nothing was deleted.');
      setRetireConfirmId(null);
      refresh();
    } catch (e: any) {
      setStatus(actionErrorMessage(e, PERMISSION_LABEL.documents, 'retire forms', 'Failed to retire the form.'));
    }
  }

  // October 10 14:05 addendum: confirmed, audited HR Reactivate. Never auto-assigns the form
  // or restores any employee record — it only makes the template assignable again going
  // forward, mirroring Retire's own symmetrical "future assignability only" scope.
  async function handleReactivate(templateId: string, name: string) {
    setReactivating(templateId);
    try {
      await reactivateHrDocumentTemplate(templateId);
      setStatus(`"${name}" reactivated — assignable again going forward. This does not auto-assign the form or restore any employee record.`);
      refresh();
    } catch (e: any) {
      setStatus(actionErrorMessage(e, PERMISSION_LABEL.documents, 'reactivate forms', 'Failed to reactivate the form.'));
    } finally {
      setReactivating(null);
    }
  }

  // October 8 21:19 addendum discipline, reused here: never silently offer a preview/download
  // that will not open — check the actual bytes before opening, rather than trusting the
  // server's declared Content-Type.
  // October 10 12:24 addendum: Preview and Download are now two distinct actions against the
  // SAME version/bytes — only the requested disposition differs ('inline' opens in a new
  // browser tab using the browser's own viewer; 'attachment' forces a named save).
  async function handleOpen(templateId: string, versionId: string, label: string, disposition: 'inline' | 'attachment') {
    setDownloadNotice((m) => ({ ...m, [versionId]: disposition === 'inline' ? 'Checking file…' : 'Preparing download…' }));
    try {
      const { url, fileName } = await downloadHrDocumentTemplateVersion(templateId, versionId, disposition);
      const result = await checkDownloadUrl(url);
      if (!result.looksValid) {
        setDownloadNotice((m) => ({ ...m, [versionId]: `"${fileName}" (${label}) could not be opened: ${result.reason}` }));
        return;
      }
      setDownloadNotice((m) => ({ ...m, [versionId]: '' }));
      window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      setDownloadNotice((m) => ({ ...m, [versionId]: e.message || 'Failed to prepare the file.' }));
    }
  }

  // Cycle 12: HR marks a form as "suggested for onboarding" (a pre-selection hint in the guided setup's Documents step; it never assigns anything). Saved through the
  // existing template update route with ONLY the flag changed (name/description sent as they are saved); the list is then re-read, so the checkbox shows what is saved.
  async function handleToggleSuggested(t: HrDocumentTemplate, next: boolean) {
    if (suggestBusy) return;
    setSuggestBusy(t.templateId);
    setSuggestError((m) => ({ ...m, [t.templateId]: '' }));
    try {
      await updateHrDocumentTemplate(t.templateId, { name: t.name, description: t.description ?? undefined, suggestedForOnboarding: next });
      setStatus(next
        ? `"${t.name}" is now suggested for onboarding. It is only pre-selected when HR sets up a new employee; nothing is assigned automatically.`
        : `"${t.name}" is no longer suggested for onboarding.`);
      await refresh();
    } catch (e: any) {
      setSuggestError((m) => ({ ...m, [t.templateId]: actionErrorMessage(e, PERMISSION_LABEL.documents, 'change the Form Library', 'The suggestion could not be saved.') }));
    } finally {
      setSuggestBusy(null);
    }
  }

  async function handleAssign(template: HrDocumentTemplate) {
    const latestVersion = template.versions[0];
    if (!assignEmployeeId || !latestVersion || assigning) return;
    const prompts = liveSigning[latestVersion.versionId]?.assignmentPrompts ?? [];
    const missing = missingAssignmentValues(prompts, assignValues);
    if (missing.length) { setStatus(`Enter ${missing.join(', ')} before assigning this form.`); return; }
    setAssigning(true);
    try {
      await assignHrDocument(assignEmployeeId, template.templateId, latestVersion.versionId, Object.fromEntries(prompts.map((p) => [p.fieldKey, (assignValues[p.fieldKey] ?? '').trim()]).filter(([, v]) => v)));
      setStatus(`Assigned "${template.name}" (v${latestVersion.versionNumber}). Opening that Employee's Documents & Signatures…`);
      setAssignOpenId(null);
      const employeeId = assignEmployeeId;
      setAssignEmployeeId('');
      setAssignValues({});
      refresh();
      // Hand off to the existing employee-scoped workflow (download/print, employee or HR-on-
      // behalf scan upload, HR review) — the Library itself never duplicates that UI.
      navigate(`/hr/admin/employees/${employeeId}`);
    } catch (e: any) {
      setStatus(e?.status === 403
        ? actionErrorMessage(e, PERMISSION_LABEL.documents, 'assign forms', '')
        : e.message || 'Failed to assign the form. If this Employee already has an outstanding copy of it, open their Documents & Signatures section instead of assigning again.');
    } finally {
      setAssigning(false);
    }
  }

  function renderTemplateList(list: HrDocumentTemplate[], section: 'active' | 'retired') {
    return (
      <ul className="divide-y divide-gray-100">
        {list.map((t) => {
          const latest = t.versions[0];
          const olderVersions = t.versions.slice(1);
          const historyOpen = historyOpenId === t.templateId;
          const assignOpen = assignOpenId === t.templateId;
          const isOpen = expandedIds.has(t.templateId);
          const retireConfirmOpen = retireConfirmId === t.templateId;
          return (
            <li key={t.templateId} className="py-1.5" data-testid={`form-row-${t.templateId}`}>
              {/* Oct 17 (Owner request): ONE clean line per form. Left: a disclosure button (chevron + bold name + status chips).
                  Right: three small icon buttons - Preview, Download, Assign. Everything else (description, version, version history,
                  Signing setup, Edit, Replace, Retire / Reactivate) lives in the drop-down under the line, so many forms fit on one page. */}
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => toggleExpanded(t.templateId)}
                  aria-expanded={isOpen}
                  aria-controls={`form-details-${t.templateId}`}
                  data-testid={`form-toggle-${t.templateId}`}
                  className="flex items-center gap-2 min-w-0 flex-1 text-left rounded px-1 py-1 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]"
                >
                  <ChevronIcon open={isOpen} />
                  <span className="text-sm font-semibold text-gray-900 truncate">{t.name}</span>
                  {t.retiredAt && <span className="text-[10px] font-normal text-gray-500 bg-gray-100 rounded px-1.5 py-0.5 flex-shrink-0">Retired</span>}
                  {t.recordKind === 'equipment' && <span className="text-[10px] font-normal text-gray-500 bg-gray-100 rounded px-1.5 py-0.5 flex-shrink-0">Equipment form</span>}
                  {t.suggestedForOnboarding === true && !t.retiredAt && (
                    <span className="text-[10px] font-normal text-[#176A9B] bg-[#EDF5FC] border border-[#B9DAF0] rounded px-1.5 py-0.5 flex-shrink-0" data-testid={`suggested-badge-${t.templateId}`}>Suggested for onboarding</span>
                  )}
                  {latest && liveSigning[latest.versionId] && (
                    <span className="text-[10px] font-normal text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 flex-shrink-0" data-testid={`signing-chip-${t.templateId}`}>
                      Signing configured
                    </span>
                  )}
                  {latest && liveSigning[latest.versionId]?.needsCorrection && (
                    <span className="text-[10px] font-normal text-amber-800 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5 flex-shrink-0" data-testid={`signing-needs-correction-${t.templateId}`} title="Saved before values set by HR were made read-only for the employee. Open Signing setup, review the flagged fields and publish again before assigning.">
                      Signing setup needs review
                    </span>
                  )}
                  {/* Oct 17 (Owner request): a yellow flag when an e-signable PDF has no live signing setup yet (it is assigned on paper until set up).
                      Not shown for retired forms, the generated equipment form, or files that are not PDFs (those can't be set up for e-signing). */}
                  {latest && !t.retiredAt && t.recordKind !== 'equipment' && latest.contentType === 'application/pdf' && !liveSigning[latest.versionId] && (
                    <span className="text-[10px] font-normal text-amber-800 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5 flex-shrink-0" data-testid={`signing-unconfigured-${t.templateId}`}>
                      Signing not configured
                    </span>
                  )}
                </button>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  {latest && (
                    <>
                      <button type="button" onClick={() => handleOpen(t.templateId, latest.versionId, `current, v${latest.versionNumber}`, 'inline')}
                        aria-label={`Preview ${t.name}`} title="Preview" data-testid={`form-preview-${t.templateId}`} className={ICON_BTN}>
                        <EyeIcon />
                      </button>
                      <button type="button" onClick={() => handleOpen(t.templateId, latest.versionId, `current, v${latest.versionNumber}`, 'attachment')}
                        aria-label={`Download ${t.name}`} title="Download" data-testid={`form-download-${t.templateId}`} className={ICON_BTN}>
                        <DownloadIcon />
                      </button>
                    </>
                  )}
                  {section === 'active' && (
                    <button type="button" onClick={() => { setAssignOpenId(assignOpen ? null : t.templateId); setAssignEmployeeId(''); setAssignValues({}); }}
                      disabled={cannotManage}
                      aria-label={`Assign ${t.name} to an employee`} aria-expanded={assignOpen} title={cannotManage ? permissionMessage : 'Assign to employee'} data-testid={`form-assign-${t.templateId}`}
                      className={`${ICON_BTN} text-[#176A9B] disabled:opacity-40 disabled:cursor-not-allowed`}>
                      <UserPlusIcon />
                    </button>
                  )}
                </div>
              </div>

              {latest && downloadNotice[latest.versionId] && (
                <div className="mt-1 ml-7 text-xs text-amber-700">{downloadNotice[latest.versionId]}</div>
              )}

              {isOpen && (
                <div id={`form-details-${t.templateId}`} className="mt-1.5 ml-7 mb-1 space-y-2" data-testid={`form-details-${t.templateId}`}>
                  {/* Oct 17 (Owner request): no description / file name here - just the current version and WHEN it was added. */}
                  <div className="text-xs text-gray-500" data-testid={`form-version-${t.templateId}`}>
                    Current version: v{latest?.versionNumber}{latest?.uploadedAt ? <> · added {formatLocalDate(latest.uploadedAt)}</> : null}
                    {olderVersions.length > 0 && <> · {olderVersions.length} earlier version{olderVersions.length === 1 ? '' : 's'} on file</>}
                  </div>
                  {/* Cycle 12: only for callers who may manage forms, and only for active forms that can be assigned (not the generated equipment form). */}
                  {section === 'active' && !cannotManage && t.recordKind !== 'equipment' && (
                    <div className="text-xs" data-testid={`form-suggest-${t.templateId}`}>
                      <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer">
                        <input
                          type="checkbox" className="h-4 w-4 accent-[#2F7FB8]"
                          checked={t.suggestedForOnboarding === true}
                          disabled={suggestBusy !== null}
                          onChange={(e) => handleToggleSuggested(t, e.target.checked)}
                          aria-describedby={`form-suggest-help-${t.templateId}`}
                          data-testid={`form-suggest-toggle-${t.templateId}`}
                        />
                        <span>{suggestBusy === t.templateId ? 'Saving…' : 'Suggest for onboarding'}</span>
                      </label>
                      <p id={`form-suggest-help-${t.templateId}`} className="ml-6 text-gray-500">Pre-selects this form when HR sets up a new employee. It is never assigned until HR confirms.</p>
                      {suggestError[t.templateId] && <p role="alert" className="ml-6 text-red-700" data-testid={`form-suggest-error-${t.templateId}`}>{suggestError[t.templateId]}</p>}
                    </div>
                  )}
                  <div className="flex gap-2 items-center text-xs flex-wrap">
                    {section === 'active' && (
                      <>
                        {latest && t.recordKind !== 'equipment' && latest.contentType === 'application/pdf' && (
                          <button type="button" onClick={() => setSigningSetupTarget({ templateId: t.templateId, versionId: latest.versionId })}
                            disabled={cannotManage} title={cannotManage ? permissionMessage : undefined}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-2.5 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed" data-testid={`set-up-signing-${t.templateId}`}>
                            {liveSigning[latest.versionId] ? 'Signing setup' : 'Set up signing'}
                          </button>
                        )}
                        <button type="button" onClick={() => setEditModalTemplateId(t.templateId)} disabled={cannotManage} title={cannotManage ? permissionMessage : undefined} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-2.5 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed">
                          Edit
                        </button>
                        <button type="button" onClick={() => setReplaceModalTemplateId(t.templateId)} disabled={cannotManage} title={cannotManage ? permissionMessage : undefined} data-testid={`form-replace-${t.templateId}`} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-2.5 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed">
                          Replace
                        </button>
                        {/* Small trash/archive control - accessible name + tooltip; opens the same explicit confirmation below. */}
                        <button
                          type="button"
                          onClick={() => setRetireConfirmId(retireConfirmOpen ? null : t.templateId)}
                          disabled={cannotManage}
                          aria-label={`Retire ${t.name}`}
                          title={cannotManage ? permissionMessage : 'Retire - stops future assignment and preserves employee documents'}
                          data-testid={`form-retire-${t.templateId}`}
                          className="inline-flex items-center gap-1 text-gray-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <TrashIcon />
                          <span>Retire</span>
                        </button>
                      </>
                    )}
                    {section === 'retired' && (
                      <button
                        type="button"
                        onClick={() => handleReactivate(t.templateId, t.name)}
                        disabled={reactivating === t.templateId || cannotManage}
                        title={cannotManage ? permissionMessage : undefined}
                        className="bg-green-600 disabled:cursor-not-allowed hover:bg-green-700 text-white px-2.5 py-1 rounded disabled:opacity-50"
                      >
                        {reactivating === t.templateId ? 'Reactivating…' : 'Reactivate'}
                      </button>
                    )}
                    {olderVersions.length > 0 && (
                      <button type="button" onClick={() => setHistoryOpenId(historyOpen ? null : t.templateId)} className="text-gray-500 hover:text-gray-700 underline">
                        {historyOpen ? 'Hide' : 'Show'} version history
                      </button>
                    )}
                  </div>
                </div>
              )}

              {assignOpen && (
                <div className="mt-2 bg-[#EDF5FC] rounded-md p-3 flex items-center gap-2 flex-wrap">
                  <EmployeePicker value={assignEmployeeId} onChange={setAssignEmployeeId} placeholder="Search employee by name…" />
                  <button
                    type="button"
                    onClick={() => handleAssign(t)}
                    disabled={!assignEmployeeId || assigning || (!!latest && missingAssignmentValues(liveSigning[latest.versionId]?.assignmentPrompts ?? [], assignValues).length > 0)}
                    className="bg-[#4AABDE] text-white text-xs px-3 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {assigning ? 'Assigning…' : `Assign v${latest?.versionNumber}`}
                  </button>
                  <span className="text-xs text-gray-500">
                    {latest && liveSigning[latest.versionId]
                      ? 'Assigns the current version with its e-signature setup: the employee reviews and signs it electronically (paper stays available), then HR reviews the signed copy.'
                      : 'Assigns the current version. The employee downloads/prints it, signs on paper, and returns a scan for HR review.'}
                  </span>
                  {latest && liveSigning[latest.versionId] && (
                    <AssignValueInputs
                      prompts={liveSigning[latest.versionId]!.assignmentPrompts ?? []}
                      summary={liveSigning[latest.versionId]!.assignmentSummary ?? null}
                      values={assignValues} onChange={setAssignValues} idPrefix={`assign-${t.templateId}`}
                    />
                  )}
                </div>
              )}

              {historyOpen && (
                <ul className="mt-2 ml-0 pl-3 border-l-2 border-gray-100 space-y-1.5">
                  {olderVersions.map((v) => (
                    <li key={v.versionId} className="text-xs">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-gray-500">v{v.versionNumber} — {v.fileName}, uploaded {formatLocalDate(v.uploadedAt)}</span>
                        <button type="button" onClick={() => handleOpen(t.templateId, v.versionId, `earlier, v${v.versionNumber}`, 'inline')} className="text-[#4AABDE] hover:underline">
                          Preview
                        </button>
                        <button type="button" onClick={() => handleOpen(t.templateId, v.versionId, `earlier, v${v.versionNumber}`, 'attachment')} className="text-[#4AABDE] hover:underline">
                          Download
                        </button>
                      </div>
                      {downloadNotice[v.versionId] && <div className="text-amber-700 mt-0.5">{downloadNotice[v.versionId]}</div>}
                    </li>
                  ))}
                </ul>
              )}

              {/* Retire now requires an explicit second click. Clicking the archive icon shows
                  an inline confirmation bar; nothing happens until "Confirm retire" is
                  clicked, and "Cancel" leaves the form exactly as it was. */}
              {section === 'active' && retireConfirmOpen && (
                <div className="mt-2 bg-red-50 rounded-md px-3 py-2 flex items-center gap-2 flex-wrap text-xs">
                  <span className="text-red-700">
                    Retire "{t.name}"? This stops future assignment and preserves employee documents — existing assignments and all version history stay exactly as they are; nothing is deleted.
                  </span>
                  <button type="button" onClick={() => setRetireConfirmId(null)} className="text-gray-600 hover:underline">Cancel</button>
                  <button type="button" onClick={() => handleConfirmRetire(t.templateId)} className="bg-red-600 text-white px-2.5 py-1 rounded">
                    Confirm retire
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  const allActiveTemplates = (templates ?? []).filter((t) => !t.retiredAt);
  const retiredTemplates = (templates ?? []).filter((t) => t.retiredAt);
  const replaceTemplate = (templates ?? []).find((t) => t.templateId === replaceModalTemplateId) ?? null;
  const editTemplate = (templates ?? []).find((t) => t.templateId === editModalTemplateId) ?? null;

  // October 10 14:08 addendum: compact search over title/description/filename, replacing the
  // redundant inner "Library" heading. Search applies to the ACTIVE list only — the retired
  // list stays a separate, deliberately low-traffic collapsed section below.
  const searchLower = search.trim().toLowerCase();
  const activeTemplates = useMemo(() => {
    if (!searchLower) return allActiveTemplates;
    return allActiveTemplates.filter((t) =>
      t.name.toLowerCase().includes(searchLower)
      || (t.description ?? '').toLowerCase().includes(searchLower)
      || t.versions.some((v) => v.fileName.toLowerCase().includes(searchLower))
    );
  }, [allActiveTemplates, searchLower]);

  return (
    <div>
      {cameFrom?.returnTo && cameFrom.returnTo.startsWith('/hr/admin/employees/') && (
        <button type="button" className="text-sm text-[#176A9B] hover:underline mb-3" onClick={() => navigate(cameFrom.returnTo as string)} data-testid="back-to-employee">
          ← Back to {cameFrom.employeeName ? `${cameFrom.employeeName}’s` : 'the employee’s'} setup
        </button>
      )}
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900 leading-tight">Form Library</h1>
          <p className="text-sm text-gray-500 mt-1">Manage forms and assign them to employees.</p>
        </div>
        <button type="button" onClick={() => setAddModalOpen(true)} disabled={cannotManage} title={cannotManage ? permissionMessage : undefined} data-testid="form-library-add"
          className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed">
          Add a form
        </button>
      </div>
      {cannotManage && <div className="mb-3 -mt-3"><PermissionHint label={PERMISSION_LABEL.documents} what="change the Form Library" testId="form-library-needs-permission" /></div>}

      {status && <div className="text-sm text-gray-600 mb-3">{status}</div>}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        {/* October 10 14:08 addendum: the redundant inner "Library" heading is replaced by a
            compact search box over title/description/filename. */}
        {templates !== null && templates.length > 0 && (
          <div className="mb-3">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search forms by title, description, or file name…"
              aria-label="Search forms by title, description, or file name"
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
            />
          </div>
        )}
        {templates === null && (view.kind === 'denied' || view.kind === 'unavailable') ? (
          <LoadViewNotice view={view} thing="the Form Library" permission={PERMISSION_LABEL.documents} onRetry={() => { setView({ kind: 'loading' }); void refresh(); }} testIdPrefix="form-library" />
        ) : templates === null ? (
          <div className="text-sm text-gray-400 py-2">Loading…</div>
        ) : templates.length === 0 ? (
          <div className="text-sm text-gray-400 py-2">No forms yet. Click "Add a form" above — Chris and Builder are preparing the originals separately; nothing is pre-loaded here.</div>
        ) : activeTemplates.length === 0 ? (
          <div className="text-sm text-gray-400 py-2">{searchLower ? 'No forms match your search.' : 'No active forms — see "See retired forms" below.'}</div>
        ) : (
          renderTemplateList(activeTemplates, 'active')
        )}
      </div>

      <details className="text-xs text-gray-500 mt-4">
        <summary className="text-[#4AABDE] cursor-pointer">How this works</summary>
        <div className="mt-2 max-w-lg space-y-1.5">
          <p>Preview opens the file in a new browser tab. Download saves it with its real file name — use whichever fits what you're doing.</p>
          <p>Replacing a form never alters or deletes an earlier version — every version stays on file and previewable. "Current version" is what gets assigned next; an existing assignment keeps whatever version it was given, even after a replacement is uploaded.</p>
          <p>Assigning a form here sends you to that Employee's own Documents &amp; Signatures section, where the actual download/print, scan upload, and HR review happen — the Library itself only holds the blank originals.</p>
          <p>Retiring stops future assignment and preserves employee documents, after you confirm it — it never affects an Employee who already has this form assigned, and never deletes any version. A retired form can be reactivated, which only makes it assignable again — it never auto-assigns the form or restores any employee record.</p>
        </div>
      </details>

      {/* October 10 14:05 addendum: collapsed "See retired forms (count)" below "How this
          works", keeping Preview/Download/version info for every retired form. */}
      {retiredTemplates.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setRetiredOpen((v) => !v)}
            className="text-xs font-semibold text-gray-500 hover:text-gray-700"
          >
            {retiredOpen ? '▾' : '▸'} See retired forms ({retiredTemplates.length})
          </button>
          {retiredOpen && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mt-2">
              {renderTemplateList(retiredTemplates, 'retired')}
            </div>
          )}
        </div>
      )}

      {addModalOpen && (
        <AddFormModal
          onClose={() => setAddModalOpen(false)}
          onCreated={(message) => { setStatus(message); refresh(); }}
        />
      )}
      {replaceTemplate && (
        <ReplaceFormModal
          template={replaceTemplate}
          onClose={() => setReplaceModalTemplateId(null)}
          onReplaced={(message) => { setStatus(message); refresh(); }}
        />
      )}
      {signingSetupTarget && (() => {
        const tpl = (templates ?? []).find((x) => x.templateId === signingSetupTarget.templateId);
        const ver = tpl?.versions.find((v) => v.versionId === signingSetupTarget.versionId);
        return tpl && ver ? (
          <SigningSetupPanel
            templateId={tpl.templateId} versionId={ver.versionId} templateName={tpl.name} versionNumber={ver.versionNumber}
            onClose={() => setSigningSetupTarget(null)}
            onChanged={() => { void refreshSigning(); }}
          />
        ) : null;
      })()}
      {editTemplate && (
        <EditFormModal
          template={editTemplate}
          onClose={() => setEditModalTemplateId(null)}
          onSaved={(message) => { setStatus(message); refresh(); }}
        />
      )}
    </div>
  );
}
